import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFSelectNew from "@/components/Input/SelectNew";
import AreaPermissionsEditor from "@/components/AreaPermissionsEditor";

import {UserAPI} from "@/api/user";
import {RosterAPI} from "@/api/roster";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	getInternalClubId,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";
import {usePlayers, useStaffs, useUserRoles} from "@/utils/hooks";

const rosterAllAccessOptions = [
	{value: true, label: "Accesso a tutte le rose"},
	{value: false, label: "Rose specifiche"},
];

const HandleUserPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);

	const {players} = usePlayers({
		setLoading: false,
		handleNotification: undefined,
		goalkeepers: false,
		prospect: false,
	});
	const {staffs} = useStaffs({setLoading: false, handleNotification});
	const {userRoles} = useUserRoles({setLoading: false, handleNotification});

	const [loading, setLoading] = useState(false);
	const [fields, setFields] = useState({});
	const [userId, setUserId] = useState(null);
	const [allRosters, setAllRosters] = useState([]);
	const [aclFields, setAclFields] = useState({
		rosterAllAccess: rosterAllAccessOptions[1],
		rosterIds: [],
	});
	const [permFields, setPermFields] = useState([]);
	const [rolePerms, setRolePerms] = useState([]);

	const getAllRosters = async () => {
		try {
			const response = await RosterAPI.listAllForAssignment();
			setAllRosters(response || []);
		} catch (error) {
			// non bloccante
		}
	};

	const getUser = async () => {
		setLoading(true);
		try {
			const response = await UserAPI.getById(id);
			const resolvedUserId = response.userId || response.id || null;
			setUserId(resolvedUserId);
			setFields({
				...response,
				role: response.role
					? {value: response.role.id, label: response.role.name}
					: undefined,
				staff: response.staffId
					? {value: response.staffId, label: response.staffId}
					: undefined,
				player: response.playerId
					? {value: response.playerId, label: response.playerId}
					: undefined,
			});
			setAclFields({
				rosterAllAccess:
					rosterAllAccessOptions.find(
						(o) => o.value === response.rosterAllAccess,
					) || rosterAllAccessOptions[1],
				rosterIds: (response.assignedRosters || []).map((r) => ({
					value: r.rosterId,
					label: r.roster?.name,
				})),
			});
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getAllRosters();
		if (edit) {
			getUser();
		}
	}, [edit]);

	useEffect(() => {
		if (!fields.staffId && !fields.playerId) return;
		if (fields.staffId && staffs.length) {
			const found = staffs.find((s) => s.id === fields.staffId);
			if (found) {
				setFields((prev) => ({
					...prev,
					staff: {
						value: found.id,
						label:
							found.firstName + " " + found.lastName + " (" + found.role + ")",
					},
				}));
			}
		}
		if (fields.playerId && players.length) {
			const found = players.find((p) => p.id === fields.playerId);
			if (found) {
				setFields((prev) => ({
					...prev,
					player: {
						value: found.id,
						label: found.firstName + " " + found.lastName,
					},
				}));
			}
		}
	}, [staffs, players]);

	// Override permessi-area diretti dell'utente
	useEffect(() => {
		if (!edit || !userId) return;
		UserAPI.getUserPermissions(userId)
			.then((p) => setPermFields(p || []))
			.catch(() => setPermFields([]));
	}, [edit, userId]);

	// Baseline ereditata dal ruolo selezionato (per mostrare "Eredita · <livello>")
	useEffect(() => {
		const roleId = fields.role?.value;
		if (!roleId) {
			setRolePerms([]);
			return;
		}
		UserAPI.getRolePermissions(roleId)
			.then((r) => setRolePerms(r || []))
			.catch(() => setRolePerms([]));
	}, [fields.role?.value]); // eslint-disable-line react-hooks/exhaustive-deps

	// Solo le rose del club (non avversari, osservati o gruppi), stagione in corso per prima
	const rosterOptions = allRosters
		.filter((r) => r.clubId === getInternalClubId() && !r.isPool && !r.prospect)
		.sort(
			(a, b) =>
				(b.seasonId === seasonId) - (a.seasonId === seasonId) ||
				(b.season?.name || "").localeCompare(a.season?.name || "") ||
				a.name.localeCompare(b.name),
		)
		.map((r) => ({
			value: r.id,
			label: r.season?.name ? r.name + " · " + r.season.name : r.name,
		}));

	const getRequiredFields = () => {
		return edit
			? ["userName", "email", "role"]
			: ["userName", "password", "email", "role"];
	};

	const saveUser = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);

		if (!errors.length) {
			setLoading(true);

			let body = Object.assign({}, fields);
			body.roleId = body?.role?.value || undefined;
			body.staffId = body?.staff?.value || undefined;
			body.playerId = body?.player?.value || undefined;

			try {
				if (edit) {
					body.rosterAllAccess = aclFields.rosterAllAccess?.value ?? false;
					await UserAPI.update(id, body);
					if (userId) {
						await UserAPI.setUserRosters(userId, {
							rosterAllAccess: aclFields.rosterAllAccess?.value ?? false,
							rosterIds: aclFields.rosterIds?.map((r) => r.value) || [],
						});
						await UserAPI.setUserPermissions(userId, permFields);
					}
					handleNotification(SUCCESSFUL_OPERATION, "success");
					navigate("/settings/users/" + id);
				} else {
					const response = await UserAPI.create(body);
					handleNotification(SUCCESSFUL_OPERATION, "success");
					navigate("/settings/users/" + response.userName);
				}
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		saveUser();
	};

	const goBack = () => {
		if (edit) {
			navigate("/settings/users/" + id);
		} else {
			navigate("/settings/users");
		}
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: goBack,
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled: checkRequiredFields(getRequiredFields(), fields).length
				? true
				: false,
		},
	];

	const breadcrumbsElements = [{label: "Utenti", path: "/settings/users"}];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: fields?.userName || id,
				path: "/settings/users/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[...breadcrumbsElements, {label: "/"}]}
				current={edit ? "Modifica" : "Nuovo utente"}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">
					<VLFCard
						title="Informazioni principali"
						content={
							<div className="row">
								<div className="col-sm-12 col-md-6">
									{getTextField({
										fieldKey: "userName",
										label: "Nome utente",
										required: true,
										values: fields,
										setValues: setFields,
									})}
								</div>
								{/* In modifica la password si reimposta dalla scheda utente */}
								{!edit && (
									<div className="col-sm-12 col-md-6">
										{getTextField({
											fieldKey: "password",
											label: "Password",
											type: "password",
											required: true,
											values: fields,
											setValues: setFields,
										})}
									</div>
								)}
								<div className="col-sm-12 col-md-6">
									{getTextField({
										fieldKey: "email",
										label: "Email",
										required: true,
										values: fields,
										setValues: setFields,
									})}
								</div>
								<div className="col-sm-12 col-md-6">
									{getTextField({
										fieldKey: "telephone",
										label: "Telefono",
										type: "phone",
										values: fields,
										setValues: setFields,
									})}
								</div>
								<div className="col-sm-12 col-md-6">
									<VLFSelectNew
										options={userRoles.map((ur) => {
											return {
												value: ur.id,
												label: ur.name,
											};
										})}
										value={fields.role}
										onChange={(newValue) => {
											setFields({...fields, role: newValue});
										}}
										label="Ruolo"
										required
									/>
								</div>
								<div className="col-sm-12 col-md-6">
									<VLFSelectNew
										options={staffs.map((s) => {
											return {
												value: s.id,
												label:
													s.firstName + " " + s.lastName + " (" + s.role + ")",
											};
										})}
										value={fields.staff}
										onChange={(newValue) => {
											setFields({...fields, player: null, staff: newValue});
										}}
										label="Staff"
									/>
								</div>
								<div className="col-sm-12 col-md-6">
									<VLFSelectNew
										options={players.map((p) => {
											return {
												value: p.id,
												label: p.firstName + " " + p.lastName,
											};
										})}
										value={fields.player}
										onChange={(newValue) => {
											setFields({...fields, staff: null, player: newValue});
										}}
										label="Giocatore"
									/>
								</div>
							</div>
						}
						openable={false}
					/>
				</div>
				{edit && (
					<div className="col-sm-12 padding-small mt-3">
						<VLFCard
							title="Accessi rose"
							content={
								<div className="row">
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={rosterAllAccessOptions}
											sorted={false}
											value={aclFields.rosterAllAccess}
											onChange={(newValue) => {
												setAclFields({...aclFields, rosterAllAccess: newValue});
											}}
											label="Tipo accesso"
										/>
									</div>
									{aclFields.rosterAllAccess?.value === false && (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={rosterOptions}
												sorted={false}
												value={aclFields.rosterIds}
												onChange={(newValue) => {
													setAclFields({
														...aclFields,
														rosterIds: newValue || [],
													});
												}}
												isMulti
												label="Rose assegnate"
											/>
										</div>
									)}
								</div>
							}
							openable={false}
						/>
					</div>
				)}
				{edit && (
					<div className="col-sm-12 padding-small mt-3">
						<VLFCard
							title="Permessi per area"
							content={
								<AreaPermissionsEditor
									mode="user"
									value={permFields}
									baseline={rolePerms}
									onChange={setPermFields}
								/>
							}
							openable={false}
						/>
					</div>
				)}
			</div>
		</Page>
	);
};

export default HandleUserPage;
